/**
 * @file Docist Code Highlighting Controls
 * @description Adds a compact controls header (label + wrap/copy buttons) above <code> blocks.
 * Label priority: <pre class="language-XYZ"> → "Code".
 * @package Docist
 * @since 1.0.0
 * @version 1.0.0
 */

/**
 * Create a control button with base classes and ARIA label.
 *
 * @since 1.0.0
 * @param {string} cls   Additional class names to append.
 * @param {string} label Accessible label for the button.
 * @returns {HTMLButtonElement} The created button element.
 */
function createButton(cls, label) {
	const b = document.createElement('button');
	b.type = 'button';
	b.className = `docist-code-highlighting__button ${cls}`;
	b.setAttribute('aria-label', label);
	return b;
}

/**
 * Read a human-friendly language label from the <pre> element.
 * Accepts class names that start with "language-" (e.g., language-php).
 *
 * @since 1.0.0
 * @param {HTMLPreElement} pre The <pre> element that wraps the <code>.
 * @returns {string} The derived label (e.g., "php"), or an empty string if not found.
 */
function readLabelFromPre(pre) {
	// Accept a single token starting with "language-" → language-php, language-shortcode, etc.
	for (const t of pre.classList) {
		if (t.startsWith('language-')) return t.slice(9).replace(/[-_]+/g, ' ');
	}
	return '';
}

/**
 * Build and inject the controls header (label + actions) above a <code> block.
 *
 * @since 1.0.0
 * @param {HTMLPreElement} pre  The <pre> element that contains the <code>.
 * @param {HTMLElement}    code The <code> element to enhance.
 * @returns {void}
 */
function buildHeader(pre, code) {
	const bar = document.createElement('div');
	bar.className = 'docist-code-highlighting__controls';

	// label
	const meta = document.createElement('div');
	meta.className = 'docist-code-highlighting__meta';
	const label = document.createElement('span');
	label.className = 'docist-code-highlighting__language';
	label.textContent = readLabelFromPre(pre) || 'Code';
	meta.appendChild(label);

	// actions
	const actions = document.createElement('div');
	actions.className = 'docist-code-highlighting__actions';
	actions.setAttribute('role', 'group');
	actions.setAttribute('aria-label', 'Code actions');

	// wrap
	const wrapBtn = createButton('docist-code-highlighting__button--wrap', 'Wrap lines');
	wrapBtn.setAttribute('aria-pressed', 'false');
	wrapBtn.addEventListener('click', () => {
		const on = wrapBtn.getAttribute('aria-pressed') === 'true';
		const next = !on;
		wrapBtn.setAttribute('aria-pressed', String(next));
		wrapBtn.setAttribute('aria-label', next ? 'Unwrap lines' : 'Wrap lines');
		code.style.whiteSpace = next ? 'pre-wrap' : 'pre';
		code.style.wordBreak = next ? 'break-word' : 'normal';
		code.style.overflowX = next ? 'hidden' : 'auto';
	});

	// copy
	const copyBtn = createButton('docist-code-highlighting__button--copy', 'Copy');
	copyBtn.addEventListener('click', async () => {
		copyBtn.classList.remove(
			'docist-code-highlighting__button--success',
			'docist-code-highlighting__button--error'
		);
		try {
			await navigator.clipboard.writeText(code.textContent || '');
			copyBtn.classList.add('docist-code-highlighting__button--success');
			copyBtn.setAttribute('aria-label', 'Copied!');
			setTimeout(() => {
				copyBtn.classList.remove('docist-code-highlighting__button--success');
				copyBtn.setAttribute('aria-label', 'Copy');
			}, 3000);
		} catch (e) {
			copyBtn.classList.add('docist-code-highlighting__button--error');
			copyBtn.setAttribute('aria-label', 'Copy failed');
			setTimeout(() => {
				copyBtn.classList.remove('docist-code-highlighting__button--error');
				copyBtn.setAttribute('aria-label', 'Copy');
			}, 2500);
		}
	});

	actions.appendChild(wrapBtn);
	actions.appendChild(copyBtn);

	bar.appendChild(meta);
	bar.appendChild(actions);
	pre.insertBefore(bar, code);
}

/**
 * Initialize Docist code highlighting controls.
 * Enhances all <pre><code> pairs that have not yet been processed.
 *
 * @since 1.0.0
 * @returns {void}
 */
export function initDocistHighlighting() {
	// Only enhance <pre><code>; don’t double-init
	document.querySelectorAll('pre > code').forEach((code) => {
		const pre = code.parentElement;
		if (!pre || pre.classList.contains('docist-code-highlighting')) return;
		pre.classList.add('docist-code-highlighting');
		buildHeader(pre, code);
	});
}
